import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import api, { formatCurrency, formatPercent } from "../lib/api";
import { toast } from "sonner";
import PortfolioGrowthHero from "../components/PortfolioGrowthHero";
import ComplianceReadiness from "../components/ComplianceReadiness";
import MembershipCard from "../components/MembershipCard";
import MyVCCard from "../components/MyVCCard";
import MyIntroducteesCard from "../components/MyIntroducteesCard";
import OnboardingProgressBar from "../components/OnboardingProgressBar";
import OnboardingChecklist from "../components/OnboardingChecklist";
import SecurityAssurance from "../components/SecurityAssurance";
import { accountActionRequired } from "../lib/accountActionRequired";
import { checklistItemWithStaff, checklistOnHold } from "../lib/commandCenterStatusTone";
import { acknowledgedThisSession, disclaimerRedirect, DISCLOSURES_KEY } from "../lib/disclaimerGate";
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  Copy,
  QrCode,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  Clock,
  AlertCircle
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  // STARSEED-CAPMARKETS-257 — the six-item Step-1 checklist, from the SAME
  // endpoint and rendered by the SAME component as /command-center, so the
  // two surfaces cannot disagree about what is open. `null` = not loaded yet:
  // the page waits, so a member is never shown the dashboard for a frame
  // before the disclaimer gate decides. `{}` = the call failed or timed out,
  // in which case the dashboard renders without the checklist (and says so)
  // rather than locking the member out on an API blip — the backend gates
  // the agreement, the participation-agreement send and the handoff
  // regardless; this page is a prompt, not the control.
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchChecklist();
  }, []);

  const fetchChecklist = async () => {
    try {
      // The api client has no deadline of its own; without one a stalled
      // request would hold the skeleton up for as long as the connection
      // lasts, where the pre-257 dashboard rendered as soon as its own
      // call returned. A timeout rejects into the same fail-open path.
      const r = await api.get("/command-center/me", { timeout: 15000 });
      setChecklist(r.data || {});
    } catch (error) {
      // Loud, not silent: a member whose checklist did not load would
      // otherwise see a dashboard identical to the pre-257 one, which is the
      // failure this page exists to fix.
      console.error("ClientDashboard: onboarding checklist failed to load", {
        status: error?.response?.status ?? null,
        detail: error?.response?.data?.detail ?? error?.message ?? null,
      });
      toast.error("Could not load your onboarding checklist. Open the Command Center to see your steps.");
      setChecklist({});
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/dashboard/client");
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_token || "");
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  // STARSEED-CAPMARKETS-121 — waiver-aware; see lib/accountActionRequired.js
  const {
    registration: registrationActionNeeded,
    membership: membershipActionNeeded,
  } = accountActionRequired(user);

  const performance = dashboardData?.performance || {};
  const network = dashboardData?.referral_network || {};

  // Convert daily performance to chart data
  const dailyData = Object.entries(performance.daily_performance || {}).map(([date, data]) => ({
    date: date.substring(5),
    gains: data.gains,
    losses: data.losses,
    net: data.gains - data.losses
  })).slice(-14);

  // 257: the checklist rows the dashboard shows and the CTA it leads with.
  //
  // The lead is, in order:
  //   1. The Command Center's own next action, when it names a page the
  //      member can go to. The engine sets `next_action_url` on the rungs
  //      the member owns — intake, KYC, accreditation, consent, the
  //      agreement, the funding instructions, support for a closed file —
  //      so both pages name the same step from the same request; at Draft
  //      the ladder wants the intake before KYC, while checklist order lists
  //      KYC first. The one exclusion is the terminal rung, whose url is
  //      `/dashboard`: this page. A member is never told to continue here.
  //   2. Otherwise, unless the file is on HOLD (the engine's own copy says
  //      stop — see CHECKLIST_HOLD_STATUSES), the first open checklist item
  //      in checklist order that the member can act on: not the one item the
  //      status says is WITH STAFF (KYC or accreditation in review), and not
  //      a disclosures item the member already acknowledged this session
  //      (the backend can still report it open, see the gate). This is what
  //      names the disclaimer, the consent and the questionnaires while the
  //      ladder says "no action required" — the 2026-09-08 failure.
  //   3. Otherwise the Command Center entry, which explains.
  // It is never the ComplianceReadiness widget's next action, which is
  // document-pipeline-driven (`routes/compliance.py`) and hidden below
  // whenever this card leads with a step.
  const checklistItems = Array.isArray(checklist?.onboarding_checklist)
    ? checklist.onboarding_checklist
    : [];
  const openItems = checklistItems.filter((i) => !i.done);
  const engineUrl = checklist?.next_action_url;
  const engineNext = engineUrl && checklist?.next_action_label && engineUrl !== "/dashboard"
    ? { label: checklist.next_action_label, detail: checklist.next_action, path: engineUrl }
    : null;
  const withStaff = checklistItemWithStaff(checklist?.status);
  const acknowledged = acknowledgedThisSession(user?.id);
  const firstOpen = checklistOnHold(checklist?.status)
    ? null
    : openItems.find((i) => i.key !== withStaff && !(i.key === DISCLOSURES_KEY && acknowledged)) || null;
  const lead = engineNext
    || (firstOpen && { label: firstOpen.label, detail: firstOpen.detail, path: firstOpen.portal_path || "/command-center" })
    || null;

  if (loading || checklist === null) {
    return (
      <DashboardLayout title="Dashboard" subtitle="Loading...">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={`client-skel-${i}`} className="bg-surface border-white/5">
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-white/10 rounded w-24 mb-4"></div>
                  <div className="h-8 bg-white/10 rounded w-20"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  // 257 — the disclaimer is acknowledged at first login. A MEMBER whose
  // current-version acknowledgement is missing is sent to the disclaimer
  // before the dashboard, and comes back here afterwards via `next`. The
  // predicate is shared with the Command Center page (the registration
  // landing) in `lib/disclaimerGate.js`: client role only, and it stands
  // down for the session once an acknowledgement has been recorded.
  const gate = disclaimerRedirect(user, checklistItems, "/dashboard");
  if (gate) {
    return <Navigate to={gate} replace />;
  }

  return (
    <DashboardLayout 
      title={`Welcome, ${user?.full_name}`} 
      subtitle="Track your investments and referral network"
    >
      {/* 257 — the landing page IS the checklist: one row per Step-1 item
          with its deep link, the same component and payload as
          /command-center. What it renders per state is the component's
          contract (nothing without items; a strip once all are done). */}
      <OnboardingChecklist
        items={checklistItems}
        agreementInFlight={checklist?.agreement_in_flight}
        agreementStatus={checklist?.status}
      />

      {/* 4-step onboarding progress + secure-onboarding reassurance */}
      <div className="mb-6 space-y-4">
        <OnboardingProgressBar
          currentStep={
            user?.qualification_status === "qualified" ? 4
              : user?.accredited_status === "approved" ? 3
              : user?.kyc_status === "approved" ? 3
              : 2
          }
        />
        <SecurityAssurance variant="portal" />
      </div>

      {/* Primary CTA. 257: leads with the member's next step (see `lead`
          above) and goes straight to it; with no step of the member's to
          take it is the Command Center entry it always was. */}
      <Card className="mb-6 bg-slate-900 text-white border-slate-700">
        <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-slate-400">
              {lead ? "Your next step" : "EchoSense Onboarding Command Center"}
            </p>
            <p className="font-semibold mt-1 text-lg" data-testid="dashboard-next-step-label">
              {lead ? lead.label : "Your file. Your status. Your next step — always clear."}
            </p>
            <p className="text-sm text-slate-300 mt-1" data-testid="dashboard-next-step-detail">
              {lead
                ? lead.detail
                : "View your ES-VC number, current status, completed and missing steps, who is responsible, and what to do next."}
            </p>
            {openItems.length > 0 && (
              <p className="text-xs text-slate-400 mt-1" data-testid="dashboard-open-count">
                {openItems.length} of {checklistItems.length} steps still open
              </p>
            )}
          </div>
          <Button
            data-testid={lead ? "dashboard-next-step-btn" : "open-command-center-btn"}
            onClick={() => navigate(lead ? lead.path : "/command-center")}
            className="bg-white text-slate-900 hover:bg-slate-100 font-semibold shrink-0"
          >
            {lead ? `Continue: ${lead.label}` : "Open Command Center"}
          </Button>
        </CardContent>
      </Card>

      {/* Compliance Readiness — unified traffic-light view. 257: whenever
          the card above leads with a step, that card owns the call to
          action and the widget's own next-action button (document-pipeline
          -driven, and able to name the same step with a different
          destination) is hidden. With no step to lead with, it returns. */}
      <div className="mb-6">
        <ComplianceReadiness hideNextAction={!!lead} />
      </div>

      {/* Membership Fees — separate from investment capital */}
      <div className="mb-6">
        <MembershipCard />
      </div>

      {/* My VC# + Introducer (members-only attribution view) */}
      <div className="mb-6">
        <MyVCCard />
      </div>

      {/* My Introductees — who I brought in + their stage */}
      <div className="mb-6">
        <MyIntroducteesCard />
      </div>

      {/* Account Status Banner.

          STARSEED-CAPMARKETS-121: every clause below must respect the fee
          waiver. CAPMARKETS-069 taught MembershipCard about waivers but
          left this banner reading raw `registration_paid`, so a founding
          member whose fee was waived was told to "Complete registration
          payment" while the card two panels down said "Waived". Nothing
          here is actionable by a member whose fees are waived, so the
          payment clauses must not fire for them. */}
      {(registrationActionNeeded || membershipActionNeeded || user?.kyc_status !== "approved") && (
        <div className="mb-6 p-4 rounded bg-orange-500/10 border border-orange-500/30">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-orange-400" size={20} />
            <div>
              <p className="text-orange-400 font-medium">Action Required</p>
              <p className="text-gray-400 text-sm">
                {registrationActionNeeded && "Complete registration payment. "}
                {membershipActionNeeded && "Activate membership. "}
                {user?.kyc_status !== "approved" && "Complete KYC verification."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Growth Hero - animated count-up with emerald glow when scaling.
          Monaxa (FORGE-20260522-001) is intentionally omitted while accounts
          are deterministic demo data; the dedicated /partner-account page
          still surfaces demo metrics with disclosure. */}
      {/* CAPMARKETS-237. Deliberately NO `caption` prop.

          CAPMARKETS-235 taught the hero to describe a COMBINED total, and a
          hard-coded caption here overrode it — the JSX read `{caption || (…)}`
          — so a member holding $335,277.82, none of it in the Native Fund, read
          "Your balance from the Native Fund" directly beneath the figure, above
          a Native Fund card reading $0.00.

          The string shipped in the bundle and never rendered here, which is why
          grepping the deployed JS reported the fix as live. The caption now comes
          from the same place as the number it describes. */}
      <PortfolioGrowthHero
        scope="me"
        label="My Portfolio"
      />

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-surface border-white/5 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Wallet className="text-gold" size={24} />
              {performance.performance_percentage >= 0 ? (
                <ArrowUpRight className="text-emerald" size={20} />
              ) : (
                <ArrowDownRight className="text-red-400" size={20} />
              )}
            </div>
            <div className="text-3xl font-bold text-slate-900 tabular-nums">{formatCurrency(performance.current_balance)}</div>
            {/* CAPMARKETS-235. These three cards read `performance`, which is
                `get_fund_performance` over `fund_entries` — the Native Fund
                alone, with no partner capital in it.
                Unlabelled, they sat directly beneath a hero that now shows the
                member's COMBINED position, so a partner-funded member read
                "$335,277.82" and then "Current Balance $0.00" on one screen.
                Naming the scope costs two words and removes the contradiction;
                hiding them would lose a real figure. */}
            <p className="text-sm text-muted-foreground mt-1">Native Fund Balance</p>
            <div className={`text-sm mt-2 ${performance.performance_percentage >= 0 ? 'text-emerald' : 'text-red-400'}`}>
              {formatPercent(performance.performance_percentage)} overall
            </div>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-emerald" size={24} />
            </div>
            <div className="text-3xl font-bold text-emerald tabular-nums">{formatCurrency(performance.total_gains)}</div>
            <p className="text-sm text-muted-foreground mt-1">Native Fund Gains</p>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingDown className="text-red-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-red-400 tabular-nums">{formatCurrency(performance.total_losses)}</div>
            <p className="text-sm text-muted-foreground mt-1">Native Fund Losses</p>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="text-gold" size={24} />
            </div>
            <div className="text-3xl font-bold text-slate-900 tabular-nums">{(network.tier1_count || 0) + (network.tier2_count || 0)}</div>
            <p className="text-sm text-muted-foreground mt-1">My Introductions</p>
            <div className="text-xs text-gray-500 mt-2">
              T1: {network.tier1_count || 0} · T2: {network.tier2_count || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts & Referral Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Performance Chart */}
        <Card className="bg-surface border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white font-serif">Daily Performance</CardTitle>
            <CardDescription>Your gains and losses over the past 14 days</CardDescription>
          </CardHeader>
          <CardContent>
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={12} />
                  <YAxis stroke="#71717A" fontSize={12} tickFormatter={(v) => `$${v}`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '4px' }}
                    labelStyle={{ color: '#EDEDED' }}
                    formatter={(value, name) => [formatCurrency(value), name === 'gains' ? 'Gains' : 'Losses']}
                  />
                  <Bar dataKey="gains" fill="#10B981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="losses" fill="#EF4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No performance data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referral Card */}
        <Card className="bg-surface border-white/5">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <QrCode className="text-gold" size={20} />
              Your Referral Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 rounded bg-obsidian border border-white/10 text-center">
                <code className="text-xl font-mono text-gold">{user?.referral_token}</code>
              </div>
              
              <Button 
                onClick={copyReferralCode}
                variant="outline"
                className="w-full border-white/10 hover:bg-gold/10 hover:text-gold hover:border-gold/50"
                data-testid="copy-referral-code"
              >
                {copied ? <CheckCircle2 className="mr-2" size={16} /> : <Copy className="mr-2" size={16} />}
                {copied ? "Copied!" : "Copy Code"}
              </Button>

              <div className="pt-4 border-t border-white/10 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Tier 1 Referrals</span>
                  <span className="text-white font-medium">{network.tier1_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Tier 2 Referrals</span>
                  <span className="text-white font-medium">{network.tier2_count || 0}</span>
                </div>
              </div>

              <div className="p-3 rounded bg-gold/10 border border-gold/30">
                <p className="text-gold text-sm font-medium">Share your code</p>
                <p className="text-gray-400 text-xs mt-1">Introductions you make are tracked to your referral code</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              {user?.registration_paid ? (
                <CheckCircle2 className="text-emerald" size={24} />
              ) : (
                <Clock className="text-orange-400" size={24} />
              )}
              <div>
                <p className="text-white font-medium">Registration</p>
                <p className="text-gray-500 text-sm">$99 one-time fee</p>
              </div>
            </div>
            <Badge className={user?.registration_paid ? "bg-emerald/20 text-emerald" : "bg-orange-500/20 text-orange-400"}>
              {user?.registration_paid ? "Paid" : "Pending"}
            </Badge>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              {user?.membership_active ? (
                <CheckCircle2 className="text-emerald" size={24} />
              ) : (
                <Clock className="text-orange-400" size={24} />
              )}
              <div>
                <p className="text-white font-medium">Membership</p>
                <p className="text-gray-500 text-sm">$25/month</p>
              </div>
            </div>
            <Badge className={user?.membership_active ? "bg-emerald/20 text-emerald" : "bg-orange-500/20 text-orange-400"}>
              {user?.membership_active ? "Active" : "Inactive"}
            </Badge>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              {user?.kyc_status === "approved" ? (
                <CheckCircle2 className="text-emerald" size={24} />
              ) : user?.kyc_status === "pending" ? (
                <Clock className="text-orange-400" size={24} />
              ) : (
                <AlertCircle className="text-red-400" size={24} />
              )}
              <div>
                <p className="text-white font-medium">KYC Verification</p>
                <p className="text-gray-500 text-sm">Identity verification</p>
              </div>
            </div>
            <Badge 
              className={
                user?.kyc_status === "approved" 
                  ? "bg-emerald/20 text-emerald" 
                  : user?.kyc_status === "pending"
                  ? "bg-orange-500/20 text-orange-400"
                  : "bg-red-500/20 text-red-400"
              }
            >
              {user?.kyc_status || "Not Started"}
            </Badge>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ClientDashboard;
